import * as Sentry from "@sentry/react"
import { NuqsAdapter } from "nuqs/adapters/react-router/v7"
import {
  LoaderFunctionArgs,
  Outlet,
  RouterProvider,
  ScrollRestoration,
  createBrowserRouter,
  redirect,
} from "react-router"

import { url } from "@/lib/url"
import { XLIndex } from "@/src/components/admin/XL"

import { ErrorState } from "../components"
import { DocsPreview } from "../components/DocsPreview"
import { PageViewLogger } from "../components/PageViewLogger"

const Root = () => {
  return (
    <>
      <ScrollRestoration />
      <NuqsAdapter>
        <Outlet />
      </NuqsAdapter>
      <PageViewLogger />
      <DocsPreview />
    </>
  )
}

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)

const router = sentryCreateBrowserRouter([
  {
    id: "pdfs",
    children: [
      {
        id: "pdf-report-sales",
        path: "/pdf/report-sales",
        lazy: () => import("./pages/pdf/report-sales"),
      },
      {
        id: "pdf-portfolio-report-obbba",
        path: "/pdf/portfolio-report-obbba",
        lazy: () => import("./pages/pdf/report-obbba"),
      },
    ],
  },
  {
    id: "root",
    element: <Root />,
    errorElement: (
      <ErrorState
        title="Something went wrong"
        subTitle="Try again or contact an administrator"
        retryLabel="Try again"
        className="h-screen w-screen rounded-none"
        onRetry={() => window.location.reload()}
      />
    ),
    children: [
      {
        id: "public-app",
        children: [
          {
            id: "not-found",
            path: "/404",
            element: <div>404</div>,
          },
          {
            id: "login",
            path: "/login",
            lazy: () => import("./pages/login"),
          },
          {
            path: "/greystar-ev",
            loader: () => redirect("/platform"),
          },
          {
            path: "/",
            loader: () => redirect(url(["/orgs"])),
          },
          {
            id: "orgs",
            path: "/orgs",
            lazy: () => import("./pages/orgs"),
          },
          {
            id: "org-welcome",
            path: "/:orgSlug/welcome",
            lazy: () => import("./pages/org/welcome"),
          },
          // Developer routes
          {
            id: "developers",
            lazy: () => import("./pages/developer-layout"),
            children: [
              {
                path: "/:orgSlug/invest",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/origination",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
              {
                id: "invest-origination",
                path: "/:orgSlug/invest/origination",
                lazy: () => import("./pages/org/invest/origination"),
              },
              {
                path: "/:orgSlug/invest/origination/:bundlePid",
                lazy: () => import("./pages/org/invest/package-detail"),
                children: [
                  {
                    id: "invest-origination-create-bid",
                    path: "/:orgSlug/invest/origination/:bundlePid/property/:propertyPid/create-bid",
                    lazy: () => import("./pages/org/invest/new-bid"),
                  },
                  {
                    id: "invest-origination-edit-bid",
                    path: "/:orgSlug/invest/origination/:bundlePid/property/:propertyPid/bid/:bidPid",
                    lazy: () => import("./pages/org/invest/edit-bid"),
                  },
                ],
              },
              {
                id: "invest-origination-reports",
                path: "/:orgSlug/invest/origination/:bundlePid/reports",
                lazy: () => import("./pages/org/invest/package-reports"),
              },

              {
                id: "invest-development",
                path: "/:orgSlug/invest/development",
                lazy: () => import("./pages/org/invest/development"),
              },

              {
                id: "invest-settings",
                path: "/:orgSlug/invest/settings",
                lazy: () => import("./pages/org/settings"),
              },
              {
                path: "/:orgSlug/invest/:bundlePid",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/origination/[bundlePid]",
                      {
                        orgSlug: params["orgSlug"] ?? "",
                        bundlePid: params["bundlePid"] ?? "",
                      },
                    ]),
                  )
                },
                children: [
                  {
                    id: "invest-create-bid",
                    path: "/:orgSlug/invest/:bundlePid/property/:propertyPid/create-bid",
                    lazy: () => import("./pages/org/invest/create-bid"),
                  },
                  {
                    id: "invest-create-bid-spec",
                    path: "/:orgSlug/invest/:bundlePid/property/:propertyPid/create-bid/:specPid",
                    lazy: () => import("./pages/org/invest/create-bid"),
                  },
                  {
                    id: "invest-bid-detail",
                    path: "/:orgSlug/invest/:bundlePid/property/:propertyPid/bid/:bidPid",
                    lazy: () => import("./pages/org/invest/bid-detail"),
                  },
                ],
              },

              {
                path: "/:orgSlug/projects",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/development",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
              // Investor property pages (new project-based URLs)
              {
                id: "investor-project-detail-layout",
                lazy: () => import("./pages/org/invest/property-layout"),
                children: [
                  {
                    id: "invest-project-summary",
                    path: "/:orgSlug/invest/development/project/:projectPid",
                    lazy: () => import("./pages/org/invest/property-summary"),
                  },
                  {
                    id: "invest-project-documents",
                    path: "/:orgSlug/invest/development/project/:projectPid/documents",
                    lazy: () => import("./pages/org/invest/property-documents"),
                  },
                  {
                    id: "invest-project-bids",
                    path: "/:orgSlug/invest/development/project/:projectPid/bids",
                    lazy: () => import("./pages/org/invest/property-bids"),
                  },
                ],
              },
              // Legacy property page redirects (bundlePid/property/sitePid → development table)
              {
                path: "/:orgSlug/invest/development/:bundlePid/property/:propertyPid",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/development",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
              {
                path: "/:orgSlug/invest/development/:bundlePid/property/:propertyPid/documents",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/development",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
              {
                path: "/:orgSlug/invest/development/:bundlePid/property/:propertyPid/bids",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/development",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
              {
                path: "/:orgSlug/invest/:bundlePid/property/:propertyPid",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/development",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
              {
                path: "/:orgSlug/invest/:bundlePid/property/:propertyPid/documents",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/development",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
              {
                path: "/:orgSlug/invest/:bundlePid/property/:propertyPid/bids",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/development",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
            ],
          },
          // Building owner routes
          {
            id: "building-owner",
            lazy: () => import("./pages/building-owner-layout"),
            children: [
              {
                id: "org-index",
                path: "/:orgSlug",
                lazy: () => import("./pages/org/index"),
              },
              {
                id: "org-portfolio",
                path: "/:orgSlug/portfolio",
                lazy: () => import("./pages/org/portfolio"),
              },
              {
                id: "org-settings",
                path: "/:orgSlug/settings",
                lazy: () => import("./pages/org/settings"),
              },
              {
                id: "org-properties",
                path: "/:orgSlug/properties",
                lazy: () => import("./pages/org/properties/index"),
              },
              {
                id: "org-properties-add",
                path: "/:orgSlug/properties/add",
                lazy: () => import("./pages/org/properties/add/index"),
              },
              {
                id: "org-emails",
                path: "/:orgSlug/org-emails",
                lazy: () => import("./pages/org/org-emails"),
              },
              {
                id: "org-reonomy",
                path: "/:orgSlug/reonomy",
                lazy: () => import("./pages/org/properties/add/reonomy"),
              },
              {
                id: "org-uploads",
                path: "/:orgSlug/uploads",
                lazy: () => import("./pages/org/properties/add/uploads"),
              },
              {
                id: "org-upload-detail",
                path: "/:orgSlug/uploads/:csvImportPid",
                lazy: () => import("./pages/org/properties/add/upload"),
              },
              {
                id: "org-bundles",
                path: "/:orgSlug/bundles",
                lazy: () => import("./pages/org/properties/bundles/index"),
              },
              {
                id: "org-bundle-parents",
                path: "/:orgSlug/bundles/parents",
                lazy: () => import("./pages/org/properties/bundles/parents/index"),
              },
              {
                id: "org-bundle-parent-detail",
                path: "/:orgSlug/bundles/parents/:bundleParentPid",
                lazy: () => import("./pages/org/properties/bundles/parents/detail"),
              },
              {
                id: "org-bundle-parent-emails",
                path: "/:orgSlug/bundles/parents/:bundleParentPid/emails",
                lazy: () => import("./pages/org/properties/bundles/parents/emails"),
              },

              {
                id: "org-shine",
                path: "/:orgSlug/shine",
                lazy: () => import("./pages/org/shine"),
              },
              // Marketplace
              {
                id: "org-marketplace",
                path: "/:orgSlug/marketplace",
                lazy: () => import("./pages/org/marketplace/index"),
              },
              {
                id: "org-marketplace-bids",
                path: "/:orgSlug/marketplace/bids",
                lazy: () => import("./pages/org/marketplace/bids"),
              },
              {
                id: "org-marketplace-properties",
                path: "/:orgSlug/marketplace/properties",
                lazy: () => import("./pages/org/marketplace/sites"),
              },
              {
                id: "org-marketplace-packages",
                path: "/:orgSlug/marketplace/packages",
                lazy: () => import("./pages/org/marketplace/packages/index"),
              },
              {
                id: "org-marketplace-package-detail",
                path: "/:orgSlug/marketplace/packages/:bundlePid",
                lazy: () => import("./pages/org/marketplace/packages/bundle-detail"),
              },
              {
                id: "org-marketplace-package-emails",
                path: "/:orgSlug/marketplace/packages/:bundlePid/emails",
                lazy: () => import("./pages/org/marketplace/packages/bundle-emails"),
              },
              {
                path: "/:orgSlug/marketplace/packages/:bundlePid/property/:propertyPid",
                lazy: () => import("./pages/org/marketplace/packages/site/site-detail"),
                children: [
                  {
                    id: "org-marketplace-property-bid-detail",
                    path: "/:orgSlug/marketplace/packages/:bundlePid/property/:propertyPid/bid/:bidPid",
                    lazy: () =>
                      import("./pages/org/marketplace/packages/site/bid-detail"),
                  },
                ],
              },
              {
                id: "property-pages",
                path: "/:orgSlug/properties/:propertyPid",
                lazy: () => import("./pages/org/properties/detail/layout"),
                children: [
                  {
                    id: "property-redirect",
                    path: "/:orgSlug/properties/:propertyPid",
                    lazy: () => import("./pages/org/properties/detail/root"),
                  },
                  {
                    id: "property-documents",
                    path: "/:orgSlug/properties/:propertyPid/documents",
                    lazy: () => import("./pages/org/properties/detail/documents"),
                  },
                  {
                    id: "property-general",
                    path: "/:orgSlug/properties/:propertyPid/general",
                    lazy: () => import("./pages/org/properties/detail/general"),
                  },
                  {
                    id: "property-ev",
                    path: "/:orgSlug/properties/:propertyPid/ev",
                    lazy: () => import("./pages/org/properties/detail/ev"),
                  },
                  {
                    path: "/:orgSlug/properties/:propertyPid/marketplace",
                    lazy: () =>
                      import("./pages/org/properties/detail/marketplace/layout"),

                    children: [
                      {
                        id: "property-marketplace-preparing-bid-launch",
                        path: "/:orgSlug/properties/:propertyPid/marketplace/preparing-bid-launch",
                        lazy: () =>
                          import("./pages/org/properties/detail/marketplace/preparing-bid-launch"),
                      },
                      {
                        id: "property-marketplace-collecting-bids",
                        path: "/:orgSlug/properties/:propertyPid/marketplace/collecting-bids",
                        lazy: () =>
                          import("./pages/org/properties/detail/marketplace/collecting-bids"),
                      },
                      {
                        id: "property-marketplace-reviewing-bids",
                        path: "/:orgSlug/properties/:propertyPid/marketplace/reviewing-bids",
                        lazy: () =>
                          import("./pages/org/properties/detail/marketplace/reviewing-bids"),
                      },
                      {
                        id: "property-marketplace-negotiating-contracts",
                        path: "/:orgSlug/properties/:propertyPid/marketplace/negotiating-contracts",
                        lazy: () =>
                          import("./pages/org/properties/detail/marketplace/negotiating-contracts"),
                      },
                      {
                        id: "property-marketplace-developing-project",
                        path: "/:orgSlug/properties/:propertyPid/marketplace/developing-project",
                        lazy: () =>
                          import("./pages/org/properties/detail/marketplace/developing-project"),
                      },
                      {
                        id: "property-marketplace-shine",
                        path: "/:orgSlug/properties/:propertyPid/marketplace/shine",
                        lazy: () =>
                          import("./pages/org/properties/detail/marketplace/shine"),
                      },

                      // Project specific
                      {
                        path: "/:orgSlug/properties/:propertyPid/marketplace/:projectPid",

                        children: [
                          {
                            id: "property-marketplace-project-homepage",
                            path: "/:orgSlug/properties/:propertyPid/marketplace/:projectPid",
                            lazy: () =>
                              import("./pages/org/properties/detail/marketplace/project-homepage"),
                          },
                          {
                            id: "property-marketplace-project-preparing-bid-launch",
                            path: "/:orgSlug/properties/:propertyPid/marketplace/:projectPid/preparing-bid-launch",
                            lazy: () =>
                              import("./pages/org/properties/detail/marketplace/preparing-bid-launch"),
                          },
                          {
                            id: "property-marketplace-project-collecting-bids",
                            path: "/:orgSlug/properties/:propertyPid/marketplace/:projectPid/collecting-bids",
                            lazy: () =>
                              import("./pages/org/properties/detail/marketplace/collecting-bids"),
                          },
                          {
                            id: "property-marketplace-project-reviewing-bids",
                            path: "/:orgSlug/properties/:propertyPid/marketplace/:projectPid/reviewing-bids",
                            lazy: () =>
                              import("./pages/org/properties/detail/marketplace/reviewing-bids"),
                          },
                          {
                            id: "property-marketplace-project-negotiating-contracts",
                            path: "/:orgSlug/properties/:propertyPid/marketplace/:projectPid/negotiating-contracts",
                            lazy: () =>
                              import("./pages/org/properties/detail/marketplace/negotiating-contracts"),
                          },
                          {
                            id: "property-marketplace-project-developing-project",
                            path: "/:orgSlug/properties/:propertyPid/marketplace/:projectPid/developing-project",
                            lazy: () =>
                              import("./pages/org/properties/detail/marketplace/developing-project"),
                          },
                          {
                            id: "property-marketplace-project-shine",
                            path: "/:orgSlug/properties/:propertyPid/marketplace/:projectPid/shine",
                            lazy: () =>
                              import("./pages/org/properties/detail/marketplace/shine"),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "/:orgSlug/properties/:propertyPid/solar",
                    lazy: () => import("./pages/org/properties/detail/evaluate-solar"),
                    children: [
                      {
                        id: "property-solar-result",
                        path: "/:orgSlug/properties/:propertyPid/solar/:resultPid",
                        lazy: () =>
                          import("./pages/org/properties/detail/financing/detail"),
                      },
                    ],
                  },
                  {
                    path: "/:orgSlug/properties/:propertyPid/*",
                    loader: ({ params }: LoaderFunctionArgs) => {
                      return redirect(
                        url([
                          "/[orgSlug]/properties/[propertyPid]",
                          {
                            orgSlug: params["orgSlug"] ?? "",
                            propertyPid: params["propertyPid"] ?? "",
                          },
                        ]),
                      )
                    },
                  },
                  {
                    id: "property-electricity",
                    path: "/:orgSlug/properties/:propertyPid/electricity",
                    lazy: () => import("./pages/org/properties/detail/electricity"),
                  },

                  {
                    id: "property-custom-tariff",
                    path: "/:orgSlug/properties/:propertyPid/electricity/tariffs/custom/:tariffId",
                    lazy: () => import("./pages/org/properties/detail/custom_tariff"),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        id: "admin",
        children: [
          {
            id: "admin-layout",
            lazy: () => import("./pages/admin/layout"),
            children: [
              {
                id: "admin-index",
                path: "/admin",
                lazy: () => import("./pages/admin/index"),
              },
              {
                id: "admin-queue",
                path: "/admin/queue",
                lazy: () => import("./pages/admin/queue"),
                handle: { title: "Queue" },
              },
              {
                id: "admin-solar-detection",
                path: "/admin/solar-detection",
                lazy: () => import("./pages/admin/solar-detection"),
                handle: { title: "Solar detection" },
              },
              {
                id: "admin-projects",
                path: "/admin/projects",
                lazy: () => import("./pages/admin/projects"),
                handle: { title: "Marketplace" },
              },
              {
                id: "admin-documents",
                path: "/admin/documents",
                lazy: () => import("./pages/admin/documents"),
                handle: { title: "Documents" },
              },
              {
                id: "admin-document-viewer",
                path: "/admin/documents/:documentPid",
                lazy: () => import("./pages/admin/document-viewer"),
                handle: { title: "Document Viewer" },
              },
              {
                id: "admin-lease-analysis",
                path: "/admin/lease-analysis",
                lazy: () => import("./pages/admin/lease-analysis"),
                handle: { title: "Lease Analysis" },
              },
              {
                id: "admin-bulk-update",
                path: "/admin/bulk-update",
                lazy: () => import("./pages/admin/bulk-update"),
                handle: { title: "Bulk Update" },
              },
              {
                id: "admin-document-reprocess",
                path: "/admin/document-reprocess",
                lazy: () => import("./pages/admin/document-reprocess"),
                handle: { title: "Reprocess Document Analysis" },
              },
              {
                id: "admin-multiportfolio",
                path: "/admin/multiportfolio",
                lazy: () => import("./pages/admin/multiportfolio"),
                handle: { title: "Multi Portfolio" },
              },
              {
                id: "admin-impact",
                path: "/admin/impact",
                lazy: () => import("./pages/admin/impact"),
                handle: { title: "Lumen Impact" },
              },
              {
                id: "admin-finmodel",
                path: "/admin/finmodel",
                lazy: () => import("./pages/admin/finmodel/versions"),
                handle: { title: "Finmodel Testing" },
              },
              {
                id: "admin-finmodel-versions",
                path: "/admin/finmodel/versions",
                loader: () => redirect("/admin/finmodel"),
              },
              {
                id: "admin-finmodel-failures",
                path: "/admin/finmodel/failures",
                loader: () => redirect("/admin/finmodel?tab=failures"),
              },
              {
                id: "admin-finmodel-failure-detail",
                path: "/admin/finmodel/failures/:failureId",
                lazy: () => import("./pages/admin/finmodel/failures/detail"),
                handle: { title: "Finmodel failure" },
              },
              {
                id: "admin-finmodel-failures-facet",
                path: "/admin/finmodel/failures/facet",
                lazy: () => import("./pages/admin/finmodel/failures/facet"),
                handle: { title: "Finmodel failures" },
              },
              {
                id: "admin-solar-captcha",
                path: "/admin/solar-captcha",
                lazy: () => import("./pages/admin/solar-captcha"),
                handle: { title: "Solar captcha" },
              },
              {
                id: "admin-batteries",
                path: "/admin/batteries",
                lazy: () => import("./pages/admin/batteries"),
                handle: { title: "Batteries" },
              },
              {
                id: "admin-ftm-programs",
                path: "/admin/ftm-programs",
                lazy: () => import("./pages/admin/ftm-programs"),
                handle: { title: "FTM Programs" },
              },
              {
                id: "admin-users",
                path: "/admin/users",
                lazy: () => import("./pages/admin/users/index"),
                handle: { title: "Users" },
              },
              {
                id: "admin-user-detail",
                path: "/admin/users/:userId",
                lazy: () => import("./pages/admin/users/detail"),
                handle: { title: "User" },
              },
              {
                id: "admin-users-new",
                path: "/admin/users/new",
                lazy: () => import("./pages/admin/users/new"),
                handle: { title: "Add Users" },
              },
              {
                id: "admin-tariff-detail",
                path: "/admin/tariffs/:tariffId",
                lazy: () => import("./pages/admin/tariffs/detail"),
              },
              {
                id: "admin-policies",
                path: "/admin/policies",
                lazy: () => import("./pages/admin/policies/index"),
              },
              {
                id: "admin-policy-detail",
                path: "/admin/policies/:policyPid",
                lazy: () => import("./pages/admin/policies/detail"),
                handle: { title: "Policy Detail" },
              },
              {
                id: "admin-utilities",
                path: "/admin/utilities",
                lazy: () => import("./pages/admin/utilities/index"),
                handle: { title: "Utilities" },
              },
              {
                id: "admin-utility-detail",
                path: "/admin/utilities/:utilitySlug",
                lazy: () => import("./pages/admin/utilities/detail"),
              },
              {
                id: "admin-utility-tariffs",
                path: "/admin/utilities/:utilitySlug/tariffs",
                lazy: () => import("./pages/admin/utilities/tariffs"),
              },
              {
                id: "admin-orgs",
                path: "/admin/orgs",
                lazy: () => import("./pages/admin/org/index"),
                handle: { title: "Organizations" },
              },
              {
                id: "admin-org-detail",
                path: "/admin/orgs/:orgPid",
                lazy: () => import("./pages/admin/org/detail/index"),
                handle: { title: "Org Detail" },
              },
              {
                id: "admin-org-physical-check",
                path: "/admin/orgs/:orgPid/physical-check",
                lazy: () => import("./pages/admin/org/detail/physical-check"),
              },
              {
                id: "admin-org-products",
                path: "/admin/orgs/:orgPid/products",
                lazy: () => import("./pages/admin/org/detail/finproducts"),
                handle: { title: "FinProducts" },
              },
              {
                id: "admin-org-product-detail",
                path: "/admin/orgs/:orgPid/products/:productId",
                lazy: () => import("./pages/admin/org/detail/finproduct-detail"),
                handle: { title: "Org Detail" },
              },
              {
                id: "admin-portfolios",
                path: "/admin/portfolios",
                lazy: () => import("./pages/admin/portfolios/index"),
                handle: { title: "Portfolios - Admin" },
              },
              {
                id: "admin-portfolio-detail",
                path: "/admin/portfolios/:portfolioPid",
                lazy: () => import("./pages/admin/portfolios/detail"),
              },
              {
                id: "admin-portfolio-bulk",
                path: "/admin/portfolios/:portfolioPid/bulk",
                lazy: () => import("./pages/admin/portfolios/bulk"),
              },
              {
                id: "admin-portfolio-energy",
                path: "/admin/portfolios/:portfolioPid/energy",
                lazy: () => import("./pages/admin/portfolios/energy"),
              },
              {
                id: "admin-companies",
                path: "/admin/companies",
                lazy: () => import("./pages/admin/companies"),
              },
              {
                id: "admin-company-detail",
                path: "/admin/companies/:companyPid",
                lazy: () => import("./pages/admin/companies/detail"),
              },
              {
                id: "admin-buildings",
                path: "/admin/buildings",
                lazy: () => import("./pages/admin/buildings"),
              },
              {
                id: "admin-building-detail",
                path: "/admin/buildings/:buildingPid",
                lazy: () => import("./pages/admin/buildings/detail"),
              },
              {
                id: "admin-sites-redirect",
                path: "/admin/sites",
                loader: () => redirect("/admin"),
              },
              {
                id: "admin-site-layout",
                lazy: () => import("./pages/admin/sites/layout"),
                children: [
                  {
                    id: "admin-site-index",
                    path: "/admin/sites/:sitePid",
                    lazy: () => import("./pages/admin/sites/index"),
                  },
                  {
                    id: "admin-site-todos",
                    path: "/admin/sites/:sitePid/todos",
                    lazy: () => import("./pages/admin/sites/todos"),
                  },
                  {
                    id: "admin-site-policy",
                    path: "/admin/sites/:sitePid/policy",
                    lazy: () => import("./pages/admin/sites/policy"),
                  },
                  {
                    id: "admin-site-helioscopes",
                    path: "/admin/sites/:sitePid/helioscopes",
                    lazy: () => import("./pages/admin/sites/helioscopes"),
                  },
                  {
                    id: "admin-site-changelog",
                    path: "/admin/sites/:sitePid/changelog",
                    lazy: () => import("./pages/admin/sites/changelog"),
                  },
                  {
                    id: "admin-site-building-data",
                    path: "/admin/sites/:sitePid/building-data",
                    lazy: () => import("./pages/admin/sites/building-data"),
                  },
                  {
                    id: "admin-site-status",
                    path: "/admin/sites/:sitePid/status",
                    lazy: () => import("./pages/admin/sites/status"),
                  },
                  {
                    id: "admin-site-scenarios",
                    path: "/admin/sites/:sitePid/scenarios",
                    lazy: () => import("./pages/admin/sites/scenarios/index"),
                  },
                  {
                    id: "admin-site-scenario-detail",
                    path: "/admin/sites/:sitePid/scenarios/:scenarioPid",
                    lazy: () => import("./pages/admin/sites/scenarios/detail"),
                  },
                  {
                    id: "admin-site-results",
                    path: "/admin/sites/:sitePid/results",
                    lazy: () => import("./pages/admin/sites/results/index"),
                  },
                  {
                    id: "admin-site-result-detail",
                    path: "/admin/sites/:sitePid/results/:resultId",
                    lazy: () => import("./pages/admin/sites/results/detail"),
                  },
                  {
                    id: "admin-site-lux",
                    path: "/admin/sites/:sitePid/lux/:luxId",
                    lazy: () => import("./pages/admin/sites/lux"),
                  },
                  {
                    id: "admin-site-finmodels",
                    path: "/admin/sites/:sitePid/finmodels",
                    lazy: () => import("./pages/admin/sites/finmodels/index"),
                  },
                  {
                    id: "admin-site-finmodel-detail",
                    path: "/admin/sites/:sitePid/finmodels/:finmodelId",
                    lazy: () => import("./pages/admin/sites/finmodels/detail"),
                  },
                  {
                    id: "admin-site-finmodel-compare",
                    path: "/admin/sites/:sitePid/finmodels/:finmodelId/compare",
                    lazy: () => import("./pages/admin/sites/finmodels/compare"),
                  },
                  {
                    id: "admin-site-projects",
                    path: "/admin/sites/:sitePid/projects",
                    lazy: () => import("./pages/admin/sites/projects/index"),
                  },
                  {
                    id: "admin-site-etb",
                    path: "/admin/sites/:sitePid/etb",
                    lazy: () => import("./pages/admin/sites/etb/index"),
                  },
                  {
                    id: "admin-site-etb-detail",
                    path: "/admin/sites/:sitePid/etb/:etbId",
                    lazy: () => import("./pages/admin/sites/etb/detail"),
                  },
                  {
                    id: "admin-site-energy",
                    path: "/admin/sites/:sitePid/energy",
                    lazy: () => import("./pages/admin/sites/energy/index"),
                  },
                  {
                    id: "admin-site-energy-multi-meter",
                    path: "/admin/sites/:sitePid/energy/multi-meter",
                    lazy: () => import("./pages/admin/sites/energy/multi-meter/index"),
                  },
                  {
                    id: "admin-site-energy-meter-set",
                    path: "/admin/sites/:sitePid/energy/multi-meter/set/:meterSetId",
                    lazy: () => import("./pages/admin/sites/energy/multi-meter/set"),
                  },
                  {
                    id: "admin-site-energy-meter",
                    path: "/admin/sites/:sitePid/energy/multi-meter/:meterId",
                    lazy: () => import("./pages/admin/sites/energy/multi-meter/meter"),
                  },
                  {
                    id: "admin-site-energy-name-that-tariff",
                    path: "/admin/sites/:sitePid/energy/multi-meter/:meterId/name-that-tariff",
                    lazy: () =>
                      import("./pages/admin/sites/energy/multi-meter/name-that-tariff"),
                  },
                  {
                    id: "admin-site-energy-billing-meters",
                    path: "/admin/sites/:sitePid/energy/billing-meters",
                    lazy: () => import("./pages/admin/sites/energy/billing-meters"),
                  },
                  {
                    id: "admin-site-energy-conservice",
                    path: "/admin/sites/:sitePid/energy/conservice",
                    lazy: () => import("./pages/admin/sites/energy/conservice"),
                  },
                  {
                    id: "admin-site-energy-espm",
                    path: "/admin/sites/:sitePid/energy/espm",
                    lazy: () => import("./pages/admin/sites/energy/espm"),
                  },
                ],
              },
            ],
          },
          {
            id: "admin-xlsx",
            path: "/admin/xlsx",
            element: <XLIndex />,
          },
        ],
      },
    ],
  },
])

export default function ReactRouter() {
  return <RouterProvider router={router} />
}
